import { httpService } from './httpService';
import { API_BASE_URL, REQ_ENC_KEY } from './api';
import { decryptResponse, encryptResponse } from './encrypt';
import { getSessionKey } from './sessionKey';
import { BotContext } from './aiChatBotKnowledgeService';

export type AssistantStatus = 'Active' | 'InActive' | 'Deleted';
export type AssistantGender = 'female' | 'male';

export interface VoiceByGender {
  male?: string;
  female?: string;
}

export interface VoiceTtsSettings {
  provider?: string;
  language_code?: string;
  voice_name?: string;
  model_name?: string;
  prompt?: string;
  audio_encoding?: string;
  speaking_rate?: number;
  pitch?: number;
  gender?: AssistantGender;
  voice_by_gender?: VoiceByGender;
  fallback_voice?: string;
}

export interface VoiceSttSettings {
  language_code?: string;
  encoding?: string;
  model?: string;
  enable_automatic_punctuation?: boolean;
  max_alternatives?: number;
}

export interface VoiceLanguageSettings {
  target_countries?: string[];
  languages?: string[];
  multilingual_mode?: {
    enabled?: boolean;
    alternative_languages?: string[];
  };
  stt?: VoiceSttSettings;
  tts?: VoiceTtsSettings;
  task?: string;
  industry?: string;
  niche?: string;
  voice_style?: string;
}

export interface VoiceAssistantRecord {
  organization_id: string;
  application_id: string;
  bot_id: string;
  status: AssistantStatus | string;
  voice_language_settings: VoiceLanguageSettings;
  created_at?: string;
  updated_at?: string;
}

export interface VoiceAssistantLookup {
  exists: boolean;
  voice_assistant: VoiceAssistantRecord | null;
}

interface ApiErrorBody {
  errorCode?: string;
  errorDetails?: string;
}

interface ApiEnvelope {
  status: number;
  message?: string;
  data?: string;
  error?: ApiErrorBody | null;
}

export class VoiceAssistantApiError extends Error {
  statusCode?: number;

  constructor(message: string, statusCode?: number) {
    super(message);
    this.name = 'VoiceAssistantApiError';
    this.statusCode = statusCode;
  }
}

const asRecord = (value: unknown): VoiceAssistantRecord => {
  if (!value || typeof value !== 'object') {
    throw new VoiceAssistantApiError('Unexpected voice assistant response.');
  }
  const record = value as VoiceAssistantRecord;
  if (!record.bot_id) {
    throw new VoiceAssistantApiError('Unexpected voice assistant response.');
  }
  return {
    ...record,
    voice_language_settings: record.voice_language_settings || {},
  };
};

const post = async <T>(
  endpoint: string,
  body: Record<string, unknown>,
  fallback: string
): Promise<{ message: string; data: T }> => {
  try {
    const payload = { data: await encryptResponse(body, REQ_ENC_KEY) };
    const response = await httpService.post<ApiEnvelope>(`${API_BASE_URL}/user/${endpoint}`, payload);
    if (response.data?.status !== 200 || response.data.data == null) {
      throw new VoiceAssistantApiError(
        response.data?.error?.errorDetails || response.data?.message || fallback,
        response.data?.status
      );
    }
    const data = await decryptResponse<T>(response.data.data, getSessionKey() as string);
    return { message: response.data.message || '', data };
  } catch (error) {
    if (error instanceof VoiceAssistantApiError) throw error;
    const err = error as {
      response?: { data?: { message?: string; error?: ApiErrorBody } };
      message?: string;
    };
    throw new VoiceAssistantApiError(
      err.response?.data?.error?.errorDetails
      || err.response?.data?.message
      || err.message
      || fallback
    );
  }
};

export const getVoiceAssistantApiError = (error: unknown, fallback: string): string => {
  if (error instanceof VoiceAssistantApiError && error.message) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return fallback;
};

const addVoiceAssistant = (
  ctx: BotContext,
  voice_language_settings: VoiceLanguageSettings,
  status: AssistantStatus
) => post<VoiceAssistantRecord>(
  'addVoiceAssistant',
  { ...ctx, status, voice_language_settings },
  'Failed to create the AI assistant.'
);

const updateVoiceAssistant = (
  ctx: BotContext,
  voice_language_settings: VoiceLanguageSettings,
  status: AssistantStatus
) => post<VoiceAssistantRecord>(
  'updateVoiceAssistant',
  { ...ctx, status, voice_language_settings },
  'Failed to update the AI assistant.'
);

export const voiceAssistantService = {
  get: async (ctx: BotContext): Promise<VoiceAssistantLookup> => {
    const { data } = await post<VoiceAssistantLookup>(
      'getVoiceAssistant',
      { ...ctx },
      'Failed to load the AI assistant.'
    );
    if (!data?.exists || !data.voice_assistant) {
      return { exists: false, voice_assistant: null };
    }
    return { exists: true, voice_assistant: asRecord(data.voice_assistant) };
  },

  save: async (
    ctx: BotContext,
    voice_language_settings: VoiceLanguageSettings,
    status: AssistantStatus
  ): Promise<{ message: string; record: VoiceAssistantRecord }> => {
    const current = await voiceAssistantService.get(ctx);
    if (current.exists) {
      const updated = await updateVoiceAssistant(ctx, voice_language_settings, status);
      return { message: updated.message, record: asRecord(updated.data) };
    }
    try {
      const created = await addVoiceAssistant(ctx, voice_language_settings, status);
      return { message: created.message, record: asRecord(created.data) };
    } catch (error) {
      if (error instanceof VoiceAssistantApiError && error.statusCode === 409) {
        const updated = await updateVoiceAssistant(ctx, voice_language_settings, status);
        return { message: updated.message, record: asRecord(updated.data) };
      }
      throw error;
    }
  },
};
